import { Banner, DataTable, LoadingSkeleton, Tag } from '@boltenergy-be/design-system'
import styles from 'pages/app/tariffs/discounts/components/discount-history/DiscountHistory.module.scss'
import { DISCOUNT_HISTORY_COLUMNS } from './constants.ts'
import { ENERGY_TYPES, GetAppliedDiscountsResponseBody, DISCOUNT_STATUSES } from '@boltenergy-be/shared'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { DataTableRow } from '@boltenergy-be/design-system/dist/components/data-table/types'
import { useMemo } from 'react'
import { DATE_FORMAT } from 'constants/constants.ts'
import { TariffLogColumnKeys } from 'pages/app/tariffs/overview/components/tariffs-logbook/constants.ts'
import { selectCurrentServiceContracts } from 'store/contact/slice.ts'
import { useStoreSelector } from 'hooks/store.ts'
import { useGetAppliedDiscountsQuery } from 'store/queries/bolt-backend/service-contracts'

const DiscountHistory = () => {
  // Redux
  const { electricity, gas } = useStoreSelector(selectCurrentServiceContracts)

  // Queries
  const {
    data: elData,
    isLoading: elLoading,
    isError: elError
  } = useGetAppliedDiscountsQuery({ contractNumber: electricity.contractNumber }, { skip: !electricity.contractNumber })
  const {
    data: gasData,
    isLoading: gasLoading,
    isError: gasError
  } = useGetAppliedDiscountsQuery({ contractNumber: gas?.contractNumber }, { skip: !gas?.contractNumber })

  // i18n
  const { t } = useTranslation(['tariffs', 'common', 'errors'])

  // Constants
  const isLoading = elLoading || gasLoading
  const isError = elError || gasError
  const combinedData = useMemo<GetAppliedDiscountsResponseBody['discounts']>(() => {
    const list = gas && gasData?.discounts?.length ? [...(elData?.discounts || []), ...gasData.discounts] : elData?.discounts || []

    // Schwartzian transform: precompute sort keys once instead of re-parsing fromDate on every comparison
    return list
      .map((item) => ({ item, ts: dayjs(item.fromDate).unix(), isActive: item.status === DISCOUNT_STATUSES.active }))
      .sort((a, b) => (a.isActive !== b.isActive ? (a.isActive ? -1 : 1) : b.ts - a.ts))
      .map(({ item }) => item) // unwrap back to original discount objects
  }, [elData?.discounts, gas, gasData?.discounts])
  const rows = useMemo<DataTableRow<TariffLogColumnKeys>[]>(
    () =>
      combinedData.map(
        (entry) =>
          ({
            id: `${entry.energyType}-${entry.direction}-${entry.promoCode}-${dayjs(entry.fromDate).unix()}`,
            uuid: crypto.randomUUID(),
            entries: {
              [DISCOUNT_HISTORY_COLUMNS.name]: {
                header: t(($) => $.discounts.history.headers.discount),
                rawData: entry.discount,
                content: <strong>{entry.discount}</strong>
              },
              [DISCOUNT_HISTORY_COLUMNS.direction]: {
                header: t(($) => $.discounts.history.headers.direction),
                rawData: entry.direction,
                content: entry.direction ? t(($) => $.discounts.history.directions[entry.direction]) : '—'
              },
              [DISCOUNT_HISTORY_COLUMNS.status]: {
                header: t(($) => $.discounts.history.headers.status),
                rawData: entry.status,
                content: (
                  <Tag
                    type={
                      entry.status === DISCOUNT_STATUSES.active
                        ? 'positive'
                        : entry.status === DISCOUNT_STATUSES.cancelled
                          ? 'warning'
                          : 'neutral'
                    }
                    size="small"
                  >
                    {t(($) => $.discounts.history.statuses[entry.status])}
                  </Tag>
                )
              },
              [DISCOUNT_HISTORY_COLUMNS.energyType]: {
                header: t(($) => $.discounts.history.headers.energyType),
                rawData: entry.energyType,
                content: t(($) => $[entry.energyType === ENERGY_TYPES.electricity ? 'electricity' : 'gas'], { ns: 'common' })
              },
              [DISCOUNT_HISTORY_COLUMNS.start]: {
                header: t(($) => $.discounts.history.headers.start),
                rawData: entry.fromDate,
                content: entry.fromDate ? dayjs(entry.fromDate).format(DATE_FORMAT) : '—'
              },
              [DISCOUNT_HISTORY_COLUMNS.end]: {
                header: t(($) => $.discounts.history.headers.end),
                rawData: 'toDate' in entry ? entry.toDate : null,
                content: 'toDate' in entry && entry.toDate ? dayjs(entry.toDate).format(DATE_FORMAT) : '—'
              }
            }
          }) satisfies DataTableRow<TariffLogColumnKeys>
      ),
    [combinedData, t]
  )

  return isLoading ? (
    <LoadingSkeleton>
      <LoadingSkeleton.Rectangle width="100%" aspectRatio="3/1" />
    </LoadingSkeleton>
  ) : isError ? (
    <Banner
      type="blocking"
      title={t(($) => $.default, {
        ns: 'errors'
      })}
    />
  ) : combinedData?.length ? (
    <DataTable className={styles.logbook} rows={rows} />
  ) : (
    <Banner title={t(($) => $.discounts.history.noneFound)} />
  )
}

export default DiscountHistory
